/**
 * Shop timezone helpers — America/New_York (mirrors backend/src/utils/shopTimezone.ts).
 */

export const SHOP_TIMEZONE = 'America/New_York';

export type ShopCalendarParts = {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
  s: number;
};

const SHOP_DATETIME_DISPLAY = new Intl.DateTimeFormat('en-US', {
  timeZone: SHOP_TIMEZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
});

const SHOP_DATE_DISPLAY = new Intl.DateTimeFormat('en-US', {
  timeZone: SHOP_TIMEZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

const SHOP_TIME_DISPLAY = new Intl.DateTimeFormat('en-US', {
  timeZone: SHOP_TIMEZONE,
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
});

const SHOP_YMD_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: SHOP_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const SHOP_HMS_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: SHOP_TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function parse_int_part(parts: Intl.DateTimeFormatPart[], type: string): number {
  return parseInt(parts.find((p) => p.type === type)?.value || '0', 10);
}

export function shop_calendar_parts(instant: Date): ShopCalendarParts {
  const ymd = SHOP_YMD_PARTS.formatToParts(instant);
  const hms = SHOP_HMS_PARTS.formatToParts(instant);
  return {
    y: parse_int_part(ymd, 'year'),
    mo: parse_int_part(ymd, 'month'),
    d: parse_int_part(ymd, 'day'),
    h: parse_int_part(hms, 'hour'),
    mi: parse_int_part(hms, 'minute'),
    s: parse_int_part(hms, 'second'),
  };
}

export function shop_local_to_utc_ms(
  y: number,
  mo: number,
  d: number,
  h: number,
  mi: number,
  s: number,
  ms = 0
): number {
  let t = Date.UTC(y, mo - 1, d, h, mi, s, ms);
  for (let i = 0; i < 4; i++) {
    const p = shop_calendar_parts(new Date(t));
    if (p.y === y && p.mo === mo && p.d === d && p.h === h && p.mi === mi && p.s === s) {
      return t;
    }
    const got_ms = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
    const want_ms = Date.UTC(y, mo - 1, d, h, mi, s);
    t += want_ms - got_ms;
  }
  return t;
}

export function shop_ymd_from_instant(instant: Date | string): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  const p = shop_calendar_parts(d);
  return `${p.y}-${String(p.mo).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}

export function shop_today_ymd(now: Date = new Date()): string {
  return shop_ymd_from_instant(now);
}

export function parse_shop_local_datetime(local_iso: string): Date {
  const trimmed = local_iso.trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d{3}))?$/.exec(trimmed);
  if (!m) {
    throw new Error('invalid_shop_local_datetime');
  }
  return new Date(
    shop_local_to_utc_ms(
      parseInt(m[1], 10),
      parseInt(m[2], 10),
      parseInt(m[3], 10),
      parseInt(m[4], 10),
      parseInt(m[5], 10),
      parseInt(m[6] || '0', 10),
      parseInt(m[7] || '0', 10)
    )
  );
}

function to_date(instant: Date | string | null | undefined): Date | null {
  if (instant == null) return null;
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  return isNaN(d.getTime()) ? null : d;
}

export function format_utc_instant(instant: Date | string | null | undefined): string {
  const d = to_date(instant);
  if (!d) return '';
  return SHOP_DATETIME_DISPLAY.format(d);
}

export function format_utc_instant_date(instant: Date | string | null | undefined): string {
  const d = to_date(instant);
  if (!d) return '';
  return SHOP_DATE_DISPLAY.format(d);
}

export function format_utc_instant_time(instant: Date | string | null | undefined): string {
  const d = to_date(instant);
  if (!d) return '';
  return SHOP_TIME_DISPLAY.format(d);
}

export function shop_local_datetime_input_value(instant: Date | string | null | undefined): string {
  const d = to_date(instant);
  if (!d) return '';
  const p = shop_calendar_parts(d);
  return `${p.y}-${String(p.mo).padStart(2, '0')}-${String(p.d).padStart(2, '0')}T${String(p.h).padStart(2, '0')}:${String(p.mi).padStart(2, '0')}`;
}

/** Round up to next 15-minute shop-local slot from now. */
export function next_shop_local_quarter_hour(from: Date = new Date()): Date {
  const p = shop_calendar_parts(from);
  let total_minutes = p.h * 60 + p.mi;
  total_minutes = Math.ceil((total_minutes + 1) / 15) * 15;
  let h = Math.floor(total_minutes / 60) % 24;
  let mi = total_minutes % 60;
  let y = p.y;
  let mo = p.mo;
  let d = p.d;
  if (total_minutes >= 24 * 60) {
    const next_day = new Date(shop_local_to_utc_ms(y, mo, d, 23, 59, 59) + 1000);
    const np = shop_calendar_parts(next_day);
    y = np.y;
    mo = np.mo;
    d = np.d;
    h = np.h;
    mi = np.mi;
  }
  return new Date(shop_local_to_utc_ms(y, mo, d, h, mi, 0, 0));
}

/** Build shop-local wire ISO for scheduler API (no offset suffix). */
export function to_shop_local_wire_iso(instant: Date): string {
  const p = shop_calendar_parts(instant);
  return `${p.y}-${String(p.mo).padStart(2, '0')}-${String(p.d).padStart(2, '0')}T${String(p.h).padStart(2, '0')}:${String(p.mi).padStart(2, '0')}:00`;
}
